import {
  Color,
  DoubleSide,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Scene,
  Vector2,
} from "three";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { RTK_FIX_ACCURACY } from "../../../common/src/modules";
import { PHOTOSHOT_CONFIG } from "../config/ViewerConfig";
import { Viewer } from "../viewer";
import { Photoshot } from "./Photoshot";

type State = "static" | "focus";
type Accuracy = RTK_FIX_ACCURACY;

type HeadMaterialMap = {
  [key in `${State}-${Accuracy}`]?: MeshBasicMaterial;
};

type BodyMaterialMap = {
  [key in State]?: LineMaterial;
};

type FaceMaterialMap = {
  [key in State]?: MeshBasicMaterial;
};

export class PhotoshotManager {
  public list: Photoshot[] = [];

  private _canvas: HTMLCanvasElement | null = null;
  private _viewer: Viewer | null = null;

  private _headMaterials: HeadMaterialMap = {};
  private _bodyMaterials: BodyMaterialMap = {};
  private _faceMaterials: FaceMaterialMap = {};
  private _raycastMap = new Map<Object3D, Photoshot>();
  public scene = new Scene();

  init(canvas: HTMLCanvasElement, viewer: Viewer) {
    this._canvas = canvas;
    this._viewer = viewer;
    this._initConfigurations();
    // this._injectDebugs();

    canvas.addEventListener("click", this.onClick);
    canvas.addEventListener("mousemove", this.onMouseMove);
    canvas.addEventListener("mouseleave", this.clearFocus.bind(this));
    canvas.addEventListener("mouseup", this.clearFocus.bind(this));
    canvas.addEventListener("touchend", this.onTouchEnd);
    window.addEventListener("resize", this.resize);
    this.resize();

    document.addEventListener("pointloaded", (event: any) => {
      const { detail } = event;
      if (detail != "simple") {
        this.clear();
      }
    });
  }
  resize = () => {
    for (const material of Object.values(this._bodyMaterials)) {
      material.resolution.set(
        this._canvas!.clientWidth,
        this._canvas!.clientHeight
      );
    }
  };

  onTouchEnd = (event: TouchEvent) => {
    const [first] = event.changedTouches;

    if (!first) return;
    const rect = this._canvas!.getBoundingClientRect();
    const x = first.clientX - rect.left;
    const y = first.clientY - rect.top;
    this.handleClick(x, y);
  };

  onClick = (event: MouseEvent) => {
    const [x, y] = [event.offsetX, event.offsetY];
    this.handleClick(x, y);
  };

  handleClick = (x: number, y: number) => {
    const ndc = new Vector2(
      (x / this._canvas!.clientWidth) * 2 - 1,
      -(y / this._canvas!.clientHeight) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(ndc, this._viewer.getCamera());

    const clickable = this.list
      .filter((photoshot) => photoshot.visible)
      .map((photoshot) => {
        return [photoshot.head, photoshot.face];
      })
      .flat();

    const intersects = raycaster.intersectObjects(clickable, true);
    if (intersects.length > 0) {
      const intersect = intersects[0];
      const photoshot = intersect.object.parent as Photoshot;

      const event = new CustomEvent("photoshot-clicked", {
        detail: {
          photoId: photoshot.photoId,
          photoName: `${photoshot.photoName}.jpg`,
        },
      });

      window.dispatchEvent(event);
    }
  };

  onMouseMove = (event: MouseEvent) => {
    this.clearFocus();
    const [x, y] = [event.offsetX, event.offsetY];
    const ndc = new Vector2(
      (x / this._canvas!.clientWidth) * 2 - 1,
      -(y / this._canvas!.clientHeight) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(ndc, this._viewer!.getCamera());
    const intersects = raycaster.intersectObjects(
      Array.from(this._raycastMap.keys()),
      true
    );

    const [firstHit] = intersects;
    if (firstHit) {
      const photoshot = this._raycastMap.get(firstHit.object);
      if (photoshot) {
        photoshot.focused = true;
        this.update();
      }
    } else {
      this.clearFocus();
      this.update();
    }
  };

  addPhotoshot(photoshot: Photoshot): void {
    this.list.push(photoshot);
    this._raycastMap.set(photoshot.head, photoshot);
    this._raycastMap.set(photoshot.face, photoshot);
    this.scene.add(photoshot);
    this.update();
  }

  removePhotoshot(photoshot: Photoshot): void {
    this.list = this.list.filter((p) => p.photoId !== photoshot.photoId);
    this._raycastMap.delete(photoshot.head);
    this._raycastMap.delete(photoshot.face);
  }

  setVisible(visible: boolean): void {
    for (const photoshot of this.list) {
      photoshot.visible = visible;
    }
  }

  clearFocus() {
    for (const photoshot of this.list) {
      photoshot.focused = false;
    }
  }
  clear() {
    this.list = [];
    this._raycastMap.clear();
    this.scene.clear();
    this.clearFocus();
  }

  update() {
    for (const photoshot of this.list) {
      const state = photoshot.focused ? "focus" : "static";
      const accuracy = photoshot.accuracy;

      photoshot.head.material = this._headMaterials[`${state}-${accuracy}`]!;
      photoshot.body.material = this._bodyMaterials[state]!;
      photoshot.face.material = this._faceMaterials[state]!;
      photoshot.head.scale.set(
        PHOTOSHOT_CONFIG.HEAD_SIZES[accuracy],
        PHOTOSHOT_CONFIG.HEAD_SIZES[accuracy],
        PHOTOSHOT_CONFIG.HEAD_SIZES[accuracy]
      );
    }
  }

  private _initConfigurations() {
    const white = new Color(0xffffff);
    const staticLowColor = new Color(PHOTOSHOT_CONFIG.COLORS.STATIC_LOW);
    const focusLowColor = staticLowColor.clone();
    focusLowColor.lerp(white, 0.5);

    const staticMediumColor = new Color(PHOTOSHOT_CONFIG.COLORS.STATIC_MEDIUM);
    const focusMediumColor = staticMediumColor.clone();
    focusMediumColor.lerp(white, 0.5);

    const staticHighColor = new Color(PHOTOSHOT_CONFIG.COLORS.STATIC_HIGH);
    const focusHighColor = staticHighColor.clone();
    focusHighColor.lerp(white, 0.5);

    this._headMaterials = {
      "static-GPS": new MeshBasicMaterial({
        color: staticLowColor,
      }),
      "static-LOW": new MeshBasicMaterial({
        color: staticLowColor,
      }),
      "static-MEDIUM": new MeshBasicMaterial({
        color: staticMediumColor,
      }),
      "static-HIGH": new MeshBasicMaterial({
        color: staticHighColor,
      }),
      "focus-GPS": new MeshBasicMaterial({
        color: focusLowColor,
      }),
      "focus-LOW": new MeshBasicMaterial({
        color: focusLowColor,
      }),
      "focus-MEDIUM": new MeshBasicMaterial({
        color: focusMediumColor,
      }),
      "focus-HIGH": new MeshBasicMaterial({
        color: focusHighColor,
      }),
    };

    this._bodyMaterials = {
      focus: new LineMaterial({
        worldUnits: false,
        color: PHOTOSHOT_CONFIG.COLORS.BODY_FOCUS,
        linewidth: 3,
      }),

      static: new LineMaterial({
        worldUnits: false,
        color: PHOTOSHOT_CONFIG.COLORS.BODY_STATIC,
        linewidth: 1,
      }),
    };
    this._faceMaterials = {
      static: new MeshBasicMaterial({
        side: DoubleSide,
        transparent: true,
        color: PHOTOSHOT_CONFIG.COLORS.FACE_STATIC,
        opacity: 0.5,
      }),
      focus: new MeshBasicMaterial({
        side: DoubleSide,
        transparent: true,
        color: PHOTOSHOT_CONFIG.COLORS.FACE_FOCUS,
        opacity: 0.5,
      }),
    };
  }

  _injectDebugs() {
    // @ts-ignore
    window["setBodyScale"] = (x, y, z) => {
      for (const photoshot of this.list) {
        photoshot.body.scale.set(x, y, z);
        photoshot.face.scale.set(x, y, z);
      }
    };
  }
}
